import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {ShieldAlert, Ban, Flag} from 'lucide-react';
import {profileError} from '../lib/profileDraft';
import {applicationChanged} from '../lib/applicationState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = [['spam', 'İstenmeyen / tekrarlayan ileti'], ['harassment', 'Taciz veya rahatsız edici davranış'], ['fake_profile', 'Sahte profil'], ['other', 'Diğer']];
const cache = {key: '', at: 0, promise: null};

// Kayıtlı Instagram/telefon var mı ve askı durumu: değerlerin kendisi istemciye dönmez (yalnız var/yok).
export function useInteractionProfile(userId) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!userId) return undefined;
    if (cache.key !== userId || Date.now() - cache.at > 60000 || !cache.promise) {
      cache.key = userId; cache.at = Date.now();
      cache.promise = axios.get(`${API}/me/interaction`, {timeout: 15000}).then(r => r.data).catch(() => { cache.promise = null; return null; });
    }
    cache.promise.then(data => { if (alive) setState(data); });
    return () => { alive = false; };
  }, [userId]);
  return state;
}
export const refreshInteractionProfile = () => { cache.promise = null; };

export function SuspensionNotice({suspension}) {
  if (!suspension) return null;
  const ends = suspension.ends_at ? new Date(suspension.ends_at).toLocaleString('tr-TR', {timeZone: 'Europe/Istanbul'}) : null;
  return <div className="demand-card offer-suspension" role="alert" data-testid="offer-suspension">
    <strong><ShieldAlert size={18} aria-hidden="true"/> İletişim yetkin geçici olarak askıda</strong>
    <p>Gerekçe: {suspension.reason_label}. {ends ? `Askı ${ends} saatinde kendiliğinden kalkar.` : 'Askı ekibimiz inceleyip kaldırana kadar sürer.'}</p>
    <p>Bu sürede yeni teklif, hazır mesaj, yanıt, tarih önerisi ve iletişim paylaşımı yapamazsın. Geçmiş yazışmalarını okumaya devam edebilirsin.</p>
  </div>;
}

export default function OfferSafety({item, role}) {
  const [mode, setMode] = useState(''), [reason, setReason] = useState('spam'), [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false), [notice, setNotice] = useState(''), [error, setError] = useState('');
  const blockedByMe = !!item.interaction?.blocked_by_me;
  const peerRole = role === 'venue' ? 'organizer' : 'venue';
  const run = async action => {
    if (busy) return; setBusy(true); setError('');
    try {
      if (action === 'block') {
        await axios.post(`${API}/blocks`, {interest_id: item.id}, {timeout: 15000});
        setNotice('Bu hesabı engelledin. Sana yeni teklif veya mesaj gönderemez; geçmiş yazışmalar okunur kalır.');
      } else if (action === 'unblock') {
        const {data} = await axios.get(`${API}/blocks`, {timeout: 15000});
        const match = (data.items || []).find(b => b.blocked_role === peerRole && b.blocked_profile_id === item[`${peerRole}_id`]);
        if (match) await axios.delete(`${API}/blocks/${encodeURIComponent(match.id)}`, {timeout: 15000});
        setNotice('Engeli kaldırdın.');
      } else {
        const {data} = await axios.post(`${API}/reports`, {interest_id: item.id, reason, details: details.trim()}, {timeout: 15000});
        setNotice(data.already_reported ? 'Bu teklif için açık bir şikâyetin zaten var; ekibimiz inceliyor.' : 'Şikâyetin ekibimize iletildi. İnceleme bitmeden kimseye otomatik yaptırım uygulanmaz.');
        setDetails('');
      }
      setMode(''); applicationChanged();
    } catch (e) { setError(profileError(e, 'İşlem tamamlanamadı. Tekrar deneyebilirsin.')); }
    finally { setBusy(false); }
  };
  return <details className="offer-safety" data-testid="offer-safety-menu">
    <summary>Güvenlik: engelle veya şikâyet et</summary>
    {notice && <p role="status">{notice}</p>}{error && <p role="alert">{error}</p>}
    {!mode && <div className="application-buttons">
      {blockedByMe ? <button type="button" className="action-secondary" disabled={busy} onClick={() => run('unblock')} data-testid="offer-unblock">Engeli kaldır</button>
        : <button type="button" className="action-secondary" disabled={busy} onClick={() => setMode('block')} data-testid="offer-block"><Ban size={16} aria-hidden="true"/> Engelle</button>}
      <button type="button" className="action-secondary" disabled={busy} onClick={() => setMode('report')} data-testid="offer-report"><Flag size={16} aria-hidden="true"/> Şikâyet et</button>
    </div>}
    {mode === 'block' && <div role="group" aria-label="Engelleme onayı"><p>Engellediğin hesap sana yeni teklif ve hazır mesaj gönderemez; neden engellendiği ona söylenmez. Açık teklifini dilersen yine kapatabilirsin.</p>
      <div className="application-buttons"><button type="button" className="action-primary" disabled={busy} onClick={() => run('block')} data-testid="offer-block-confirm">Engelle</button>
        <button type="button" className="action-secondary" disabled={busy} onClick={() => setMode('')}>Vazgeç</button></div></div>}
    {mode === 'report' && <form onSubmit={e => { e.preventDefault(); run('report'); }} data-testid="offer-report-form">
      <label>Gerekçe<select value={reason} onChange={e => setReason(e.target.value)} data-testid="offer-report-reason">{REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Kısa açıklama (isteğe bağlı, yalnız ekibimiz görür)<textarea maxLength={300} value={details} onChange={e => setDetails(e.target.value)} data-testid="offer-report-details"/></label>
      <p className="demand-muted">Telefon, e-posta veya bağlantı yazma. Şikâyetin karşı tarafa gösterilmez.</p>
      <div className="application-buttons"><button type="submit" className="action-primary" disabled={busy} data-testid="offer-report-send">Şikâyeti gönder</button>
        <button type="button" className="action-secondary" disabled={busy} onClick={() => setMode('')}>Vazgeç</button></div></form>}
  </details>;
}
